import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, TrendingUp, CreditCard, Truck, ArrowRight,
  BarChart2, Wrench, RefreshCw, AlertTriangle, CheckCircle,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area,
} from 'recharts';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';

// ─── Couleurs ─────────────────────────────────────────────────────────────────
const PARC_COLORS = {
  location_courte_duree: '#2563EB',
  stock:                 '#10B981',
  vehicule_de_societe:   '#8B5CF6',
  client:                '#F59E0B',
  epave:                 '#EF4444',
  non_affecte:           '#94A3B8',
};
const CHART_PALETTE = [
  '#1A3A6E', '#2563EB', '#3B82F6', '#60A5FA',
  '#93C5FD', '#BFDBFE', '#1D4ED8', '#1E40AF',
  '#172554', '#0EA5E9',
];
const TYPE_COLORS = { Leasing: '#2563EB', SNC: '#10B981' };

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmt(n) {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)} k€`;
  return `${n.toFixed(0)} €`;
}
function fmtMonth(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const names = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

// ─── Composants partagés ──────────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon: Icon, accent, onClick, 'data-testid': testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="text-left bg-white rounded-2xl p-5 border transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 group w-full"
      style={{ borderColor: 'var(--border)' }}>
      <div className="flex items-start justify-between mb-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${accent}18` }}>
          <Icon size={20} style={{ color: accent }} />
        </div>
        <ArrowRight size={14} className="opacity-0 group-hover:opacity-40 transition-opacity mt-1" style={{ color: accent }} />
      </div>
      <p className="text-2xl font-bold tracking-tight mb-0.5"
        style={{ color: 'var(--text)',  }}>{value}</p>
      <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>{label}</p>
      {sub && <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>{sub}</p>}
    </button>
  );
}
function SectionTitle({ children }) {
  return (
    <h2 className="text-base font-bold mb-4" style={{ color: 'var(--text)',  }}>
      {children}
    </h2>
  );
}
function Skeleton({ className = '' }) {
  return <div className={`animate-pulse rounded-lg bg-slate-100 ${className}`} />;
}
function EmptyChart() {
  return (
    <div className="flex flex-col items-center justify-center py-10" style={{ color: 'var(--text-disabled)' }}>
      <BarChart2 size={28} className="mb-2" />
      <p className="text-xs">Aucune donnée</p>
    </div>
  );
}

// ─── Tooltips ─────────────────────────────────────────────────────────────────
function ParcTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white rounded-lg border px-3 py-2 shadow-lg text-sm" style={{ borderColor: 'var(--border)' }}>
      <p className="font-semibold" style={{ color: 'var(--text)' }}>{d.label}</p>
      <p style={{ color: 'var(--text-muted)' }}>{d.count} matériels</p>
    </div>
  );
}
function BarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg border px-3 py-2 shadow-lg text-sm" style={{ borderColor: 'var(--border)' }}>
      <p className="font-semibold mb-1" style={{ color: 'var(--text)' }}>{label}</p>
      <p style={{ color: 'var(--primary-solid)' }}>{payload[0].value} matériels</p>
    </div>
  );
}
function MensTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border rounded-xl shadow-xl px-4 py-3 text-sm" style={{ borderColor: 'var(--border)', minWidth: 160 }}>
      <p className="font-semibold mb-2" style={{ color: 'var(--text)',  }}>{label}</p>
      <p className="font-bold text-base" style={{ color: '#2563EB' }}>{fmt(payload[0].value)}</p>
      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>mensualités / mois</p>
      {d.nb_contrats > 0 && (
        <p className="text-xs mt-1 pt-1 border-t" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-light)' }}>
          {d.nb_contrats} contrat{d.nb_contrats > 1 ? 's' : ''} actif{d.nb_contrats > 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}
function PieTypeTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border rounded-xl shadow-xl px-4 py-3 text-sm" style={{ borderColor: 'var(--border)' }}>
      <p className="font-semibold mb-1" style={{ color: TYPE_COLORS[d.type] || 'var(--text)' }}>{d.type}</p>
      <p style={{ color: 'var(--text)' }}>{d.count} contrat{d.count > 1 ? 's' : ''}</p>
      <p style={{ color: 'var(--text-muted)' }}>Capital : {fmt(d.capital)}</p>
    </div>
  );
}

// ─── Légende Parc ────────────────────────────────────────────────────────────
const PARC_LABELS = {
  location_courte_duree: 'Location CT', stock: 'Stock',
  vehicule_de_societe: 'Véhicule soc.', client: 'Client', epave: 'Épave',
};
function ParcDot({ parc }) {
  const color = PARC_COLORS[parc] || PARC_COLORS.non_affecte;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full"
      style={{ background: `${color}18`, color }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
      {PARC_LABELS[parc] || 'N/A'}
    </span>
  );
}
function ParcLegend({ data }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div className="space-y-2 mt-2">
      {data.map((d) => {
        const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
        const color = PARC_COLORS[d.parc] || PARC_COLORS.non_affecte;
        return (
          <div key={d.parc} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
              <span className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{d.label}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
              </div>
              <span className="text-xs font-semibold w-8 text-right" style={{ color: 'var(--text)' }}>{d.count}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Onglet Parc ──────────────────────────────────────────────────────────────
function TabParc({ stats, loading, navigate }) {
  const total      = stats?.total ?? 0;
  const valeur     = stats?.valeur_totale ?? 0;
  const nbFins     = stats?.financements?.nb_total ?? 0;
  const nbActifs   = stats?.financements?.nb_actifs ?? 0;
  const enLocation = stats?.par_parc?.find(p => p.parc === 'location_courte_duree')?.count ?? 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32" />) : (
          <>
            <KpiCard data-testid="kpi-total-materiels" label="Matériels en parc"
              value={total.toLocaleString('fr-FR')} icon={Package} accent="var(--primary-solid)"
              onClick={() => navigate('/materiels')} />
            <KpiCard data-testid="kpi-valeur-parc" label="Valeur du parc"
              value={fmt(valeur)} sub="Valeur dépréciée" icon={TrendingUp} accent="#10B981"
              onClick={() => navigate('/materiels')} />
            <KpiCard data-testid="kpi-financements" label="Financements"
              value={nbFins.toLocaleString('fr-FR')} sub={`${nbActifs} actifs`}
              icon={CreditCard} accent="#8B5CF6" onClick={() => navigate('/financements')} />
            <KpiCard data-testid="kpi-en-location" label="En location CT"
              value={enLocation.toLocaleString('fr-FR')}
              sub={`${total > 0 ? Math.round((enLocation / total) * 100) : 0}% du parc`}
              icon={Truck} accent="#F59E0B" onClick={() => navigate('/materiels')} />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: 'var(--border)' }} data-testid="chart-parc">
          <SectionTitle>Répartition par parc</SectionTitle>
          {loading ? <Skeleton className="h-56" /> : !stats?.par_parc?.length ? <EmptyChart /> : (
            <div className="flex gap-4 items-center">
              <div className="flex-shrink-0" style={{ width: 160, height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={stats.par_parc} dataKey="count" nameKey="label"
                      cx="50%" cy="50%" innerRadius={46} outerRadius={72} paddingAngle={2} strokeWidth={0}>
                      {stats.par_parc.map((entry) => (
                        <Cell key={entry.parc} fill={PARC_COLORS[entry.parc] || PARC_COLORS.non_affecte} />
                      ))}
                    </Pie>
                    <Tooltip content={<ParcTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 min-w-0"><ParcLegend data={stats.par_parc} /></div>
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: 'var(--border)' }} data-testid="chart-marques">
          <SectionTitle>Top marques</SectionTitle>
          {loading ? <Skeleton className="h-56" /> : !stats?.par_marque?.length ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.par_marque.slice(0, 8)} layout="vertical"
                margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="marque" width={90}
                  tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => v.length > 12 ? v.slice(0, 11) + '…' : v} />
                <Tooltip content={<BarTooltip />} cursor={{ fill: 'var(--primary-50)' }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {stats.par_marque.slice(0, 8).map((_, i) => (
                    <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: 'var(--border)' }} data-testid="chart-societes">
          <SectionTitle>Matériels par société</SectionTitle>
          {loading ? <Skeleton className="h-44" /> : !stats?.par_societe?.length ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={stats.par_societe} margin={{ top: 0, right: 8, left: 0, bottom: 24 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="nom" tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                  axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                <Tooltip content={<BarTooltip />} cursor={{ fill: 'var(--primary-50)' }} />
                <Bar dataKey="count" fill="var(--primary-solid)" radius={[4, 4, 0, 0]}>
                  {stats.par_societe.map((_, i) => (
                    <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: 'var(--border)' }} data-testid="chart-familles">
          <SectionTitle>Répartition par famille</SectionTitle>
          {loading ? <Skeleton className="h-44" /> : !stats?.par_famille?.length ? <EmptyChart /> : (
            <div className="space-y-3">
              {stats.par_famille.slice(0, 6).map((f, i) => {
                const pct = stats.total > 0 ? Math.round((f.count / stats.total) * 100) : 0;
                const color = CHART_PALETTE[i % CHART_PALETTE.length];
                return (
                  <div key={f.famille}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium truncate max-w-[200px]" style={{ color: 'var(--text-secondary)' }}>{f.famille}</span>
                      <span className="font-bold flex-shrink-0 ml-2" style={{ color: 'var(--text)' }}>
                        {f.count} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({pct}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border" style={{ borderColor: 'var(--border)' }} data-testid="recent-materiels">
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <SectionTitle>Derniers matériels enregistrés</SectionTitle>
          <button onClick={() => navigate('/materiels')}
            className="text-xs font-medium flex items-center gap-1 hover:underline"
            style={{ color: 'var(--primary-solid)' }}>
            Voir tout <ArrowRight size={12} />
          </button>
        </div>
        {loading ? (
          <div className="p-5 space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : !stats?.recents?.length ? (
          <div className="text-center py-12">
            <Package size={28} className="mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun matériel importé</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {stats.recents.map((m) => (
              <button key={m.materiel_id} data-testid={`recent-mat-${m.materiel_id}`}
                onClick={() => navigate('/materiels')}
                className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-slate-50 transition-colors">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--primary-50)' }}>
                  <Wrench size={14} style={{ color: 'var(--primary-solid)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{m.nom}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {[m.marque, m.modele].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <div className="flex-shrink-0"><ParcDot parc={m.parc} /></div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Onglet Financements ──────────────────────────────────────────────────────
function TabFinancements({ navigate }) {
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('Tout');

  const load = useCallback(() => {
    setLoading(true);
    api.getFinancementsRapport()
      .then(r => setRapport(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const timeline = rapport?.mensualites_timeline || [];

  // Années disponibles
  const years = [...new Set(timeline.map(d => d.month.slice(0, 4)))].sort();

  // Données filtrées par année
  const filtered = selectedYear === 'Tout'
    ? timeline
    : timeline.filter(d => d.month.startsWith(selectedYear));

  // Tick interval pour éviter surcharge
  const tickInterval = filtered.length > 18 ? Math.floor(filtered.length / 10) : 0;

  const chartData = filtered.map(d => ({
    ...d,
    label: fmtMonth(d.month),
  }));

  const kpis = rapport?.kpis || {};
  const parType = rapport?.par_type || [];

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32" />) : (
          <>
            <KpiCard label="Capital total emprunté" value={fmt(kpis.capital_total)}
              sub={`${kpis.nb_total || 0} contrats`}
              icon={CreditCard} accent="#2563EB" onClick={() => navigate('/financements')}
              data-testid="kpi-fin-capital" />
            <KpiCard label="Charge mensuelle active" value={fmt(kpis.mensualites_actives)}
              sub="Mensualités cumulées"
              icon={TrendingUp} accent="#10B981" onClick={() => navigate('/financements')}
              data-testid="kpi-fin-mensualites" />
            <KpiCard label="Contrats actifs" value={kpis.nb_actifs || 0}
              sub={`sur ${kpis.nb_total || 0} au total`}
              icon={CheckCircle} accent="#8B5CF6" onClick={() => navigate('/financements')}
              data-testid="kpi-fin-actifs" />
            <KpiCard label="Expirant dans 90j" value={kpis.nb_expirant_bientot || 0}
              sub="À renouveler"
              icon={AlertTriangle} accent="#F59E0B" onClick={() => navigate('/financements')}
              data-testid="kpi-fin-expirant" />
          </>
        )}
      </div>

      {/* Répartition + Courbe mensualités */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Répartition Leasing/SNC */}
        <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: 'var(--border)' }} data-testid="chart-fin-type">
          <SectionTitle>Leasing vs SNC</SectionTitle>
          {loading ? <Skeleton className="h-48" /> : !parType.some(t => t.count > 0) ? <EmptyChart /> : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={parType} dataKey="count" nameKey="type"
                    cx="50%" cy="50%" innerRadius={42} outerRadius={66} paddingAngle={3} strokeWidth={0}>
                    {parType.map((t) => (
                      <Cell key={t.type} fill={TYPE_COLORS[t.type] || '#94A3B8'} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTypeTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-3">
                {parType.map(t => (
                  <div key={t.type} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: TYPE_COLORS[t.type] }} />
                      <span style={{ color: 'var(--text-secondary)' }}>{t.type}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold" style={{ color: 'var(--text)' }}>{t.count}</span>
                      <span className="ml-2" style={{ color: 'var(--text-muted)' }}>{fmt(t.capital)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Courbe mensualités */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-5 border" style={{ borderColor: 'var(--border)' }} data-testid="chart-fin-mensualites">
          <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
            <SectionTitle>Mensualités à rembourser</SectionTitle>
            {/* Filtre année */}
            {!loading && years.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {['Tout', ...years].map(y => (
                  <button key={y} type="button"
                    onClick={() => setSelectedYear(y)}
                    className="text-xs px-2.5 py-1 rounded-lg font-medium transition-all"
                    style={{
                      background: selectedYear === y ? 'var(--primary-solid)' : 'var(--bg-subtle)',
                      color: selectedYear === y ? 'white' : 'var(--text-muted)',
                    }}>
                    {y}
                  </button>
                ))}
              </div>
            )}
          </div>
          {loading ? <Skeleton className="h-52" /> : !chartData.length ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="mensGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563EB" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="label"
                  tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                  axisLine={false} tickLine={false}
                  interval={tickInterval} />
                <YAxis
                  tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                  axisLine={false} tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                  width={44} />
                <Tooltip content={<MensTooltip />} cursor={{ stroke: '#2563EB', strokeWidth: 1, strokeDasharray: '4 2' }} />
                <Area type="monotone" dataKey="total"
                  stroke="#2563EB" strokeWidth={2}
                  fill="url(#mensGradient)"
                  dot={false}
                  activeDot={{ r: 5, fill: '#2563EB', stroke: 'white', strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function RapportsMateriels() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('parc');
  const { activeAgences } = useSociete();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api.getDashboardStats()
      .then(r => setStats(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [activeAgences]);

  useEffect(() => { load(); }, [load]);

  const TABS = [
    { key: 'parc', label: 'Parc matériel' },
    { key: 'financements', label: 'Financements' },
  ];

  return (
    <div className="space-y-6" data-testid="rapports-materiels-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>
            Rapports — Matériels
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Vue analytique de votre flotte et de vos financements
          </p>
        </div>
        {activeTab === 'parc' && (
          <button data-testid="rapports-refresh-btn" onClick={load} disabled={loading}
            className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border transition-colors hover:bg-slate-50"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          Erreur de chargement. Vérifiez votre connexion.
        </div>
      )}

      {/* Onglets */}
      <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
        {TABS.map(tab => (
          <button key={tab.key} type="button"
            data-testid={`tab-rapport-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className="px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px"
            style={{
              borderColor: activeTab === tab.key ? 'var(--primary-solid)' : 'transparent',
              color: activeTab === tab.key ? 'var(--primary-solid)' : 'var(--text-muted)',
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'parc'
        ? <TabParc stats={stats} loading={loading} navigate={navigate} />
        : <TabFinancements navigate={navigate} />
      }
    </div>
  );
}
